import {
  isFqDateAttr,
  isSosNumberAttr,
} from "components/StateRegistrations/constants";
import { StateRegistrationsRes } from "store/apis/StateRegistrations";
import { EntityDataAttribute } from "types/Models/entity";

const TRACKED_ENTITY_FIELD_KEYS = [
  "date_of_incorporation",
  "tax_year",
  "tax_type",
];

const hasMissingEntityField = (
  entityAttributes: EntityDataAttribute[]
): boolean =>
  entityAttributes.some((attr) => {
    const isTracked = TRACKED_ENTITY_FIELD_KEYS.some(
      (key) => attr.name === key || attr.name.startsWith(`${key}_`)
    );
    return isTracked && !attr.value;
  });

/**
 * Mirrors the "FQ pending" / "Missing info" tags shown in the State Compliance
 * table (StateTableRow.tsx) so the entity page banner and the dashboard agree.
 * Counts at most 2: 1 for FQ pending, 1 for missing info (regardless of how
 * many individual fields are missing).
 */
export const getStateAttentionCount = (
  stateReg: StateRegistrationsRes,
  entityAttributes: EntityDataAttribute[]
): number => {
  const isWithdrawn = Boolean(stateReg.withdrawal_date);
  if (isWithdrawn) return 0;

  const sosDataAttrs = stateReg.SECRETARY_OF_STATE.data_attributes;

  const hasFqPending =
    !stateReg.is_home_state &&
    stateReg.has_sos_intake &&
    Boolean(stateReg.SECRETARY_OF_STATE.cart_status);

  const hasMissingFqDate =
    !stateReg.is_home_state &&
    sosDataAttrs.some((attr) => isFqDateAttr(attr) && !attr.value);

  const hasMissingSosNumber =
    !stateReg.is_home_state &&
    !stateReg.has_sos_intake &&
    sosDataAttrs.some((attr) => isSosNumberAttr(attr) && !attr.value);

  const hasMissingInfo = stateReg.is_home_state
    ? hasMissingEntityField(entityAttributes)
    : hasMissingFqDate || hasMissingSosNumber;

  return [hasFqPending, hasMissingInfo].filter(Boolean).length;
};
